/**
 * Header Component
 * 
 * Top navigation bar component that displays the application title and user information.
 * Provides quick access to user details and logout functionality. Positioned at the
 * top of the main layout, above the page content.
 * 
 * Features:
 * - Application title display ("管理者ダッシュボード" - Admin Dashboard)
 * - User identification with employee ID and role badge
 * - Logout button with intuitive door icon (🚪)
 * - Responsive design that integrates with the main layout
 * - Japanese language interface
 * 
 * Display elements:
 * - Title: "管理者ダッシュボード" (Admin Dashboard)
 * - User info: "👤 ID {employeeId} 管理者" (User ID and Admin role)
 * - Logout: "🚪 ログアウト" (Logout button with door icon)
 * 
 * Behavior:
 * - Displays default employee ID "000" if none provided
 * - Logout button triggers onLogout callback if provided
 * - Fully responsive and integrates with SideBar component
 * 
 * Used by:
 * - MainLayout component as top navigation bar
 * 
 * @component
 */

import React from 'react';
import './css/Header.css';

/**
 * Header Component Props Interface
 * 
 * @interface HeaderProps
 * @property {string} [employeeId] - Current user's employee ID (optional, defaults to '000')
 * @property {() => void} [onLogout] - Callback function to handle user logout (optional)
 */
/** Display label for user type in header */
const getUserTypeLabel = (userType?: string): string => {
  if (userType === '管理者') return '管理者（上位権限）';
  if (userType === '現場管理者') return '管理者（現場権限）';
  return userType || '管理者';
};

interface HeaderProps {
  employeeId?: string;
  userType?: string;
  onLogout?: () => void;
}

/**
 * Header Component
 * 
 * Top navigation bar that displays the application title and user information.
 * This component provides quick access to user details and logout functionality.
 * 
 * Features:
 * - Application title display ("管理者ダッシュボード" - Admin Dashboard)
 * - User identification with employee ID and role
 * - Logout button with intuitive icon
 * - Responsive design that integrates with the main layout
 * 
 * @param props - HeaderProps containing optional employeeId and logout handler
 * @param props.employeeId - Current user's employee ID (optional, defaults to '000')
 * @param props.onLogout - Callback function to handle user logout (optional)
 * @returns React functional component
 * 
 * @example
 * ```typescript
 * <Header employeeId="12345" onLogout={() => handleLogout()} />
 * ```
 */
export const Header: React.FC<HeaderProps> = ({ employeeId, userType, onLogout }) => {
  const roleLabel = getUserTypeLabel(userType);
  return (
    <header className="main-header" role="banner">
      <h1>管理者ダッシュボード</h1>
      
      <div className="user-info">
        <span aria-label={`User ID ${employeeId || '000'} ${roleLabel}`}>
          👤 ID {employeeId || '000'} {roleLabel}
        </span>
        
        {/* Logout Button - Door icon (🚪) for intuitive UX */}
        {/* Triggers onLogout callback when clicked */}
        <button 
          className="logout-button" 
          onClick={onLogout}
          aria-label="Logout"
          type="button"
        >
          🚪 ログアウト
        </button>
      </div>
    </header>
  );
}

